import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { useAuth } from "../../context/AuthContext";
import axiosInstance from "../../routes/axiosInstance";
import { registerPushNotifications } from "../../utils/notificationUtil";
import {
  Input,
  Button,
  Heading,
  InputGroup,
  InputRightElement,
  useToast,
  Card,
  Box,
  FormControl,
  FormLabel,
  Text,
  IconButton,
  Container,
  VStack,
  HStack,
  Link,
} from "@chakra-ui/react";

const LoginPage = () => {
  const { loginUser } = useAuth();
  const [userEmail, setUserEmail] = useState("");
  const [userPass, setUserpass] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const handleShowPass = () => setShowPass(!showPass);

  const submitLogin = async () => {
    if (!userEmail || !userPass) {
      toast({
        title: "Authentication Error",
        description: "Email and password are required",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const loginPayload = {
      email: userEmail,
      password: userPass,
    };
    setLoading(true);
    const config = { headers: { "Content-type": "application/json" } };
    try {
      const userLoginData = await axiosInstance.post(
        "/api/users/signin",
        loginPayload,
        config,
      );
      loginUser(userLoginData.data.data);

      toast({
        title: "Welcome back!",
        description: `Hello ${userLoginData.data.data.name}`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setLoading(false);
      registerPushNotifications(userLoginData.data.data);
      navigate("/chats");
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        setErrorMessage(error.response.data.error);
      } else {
        setErrorMessage(error.message);
      }
      setLoading(false);
      toast({
        title: "Login Failed",
        description: errorMessage || "Invalid email or password",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const gotoSignUpPage = () => {
    navigate("/signup");
  };

  const handleEnterKeyPress = (event) => {
    if (event.key === "Enter") {
      submitLogin();
    }
  };

  return (
    <Box bg="gray.50" minH="100vh">
      <Container
        maxW="md"
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={4}
        py={8}
      >
        <VStack spacing={6} w="100%" maxW="400px">
          {/* VartaVerse Brand Title */}
          <Heading
            fontSize="3xl"
            color="teal.500"
            fontWeight="700"
            cursor="pointer"
            onClick={() => navigate("/")}
            _hover={{
              color: "teal.600",
              transform: "scale(1.02)",
              textDecoration: "none",
            }}
            transition="all 0.2s"
            textAlign="center"
          >
            VartaVerse
          </Heading>

          {/* Login Form */}
          <Card
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            p={8}
            w="100%"
            borderRadius="lg"
            boxShadow="sm"
          >
            <VStack spacing={6}>
              <VStack spacing={2} textAlign="center" mb={2}>
                <Heading fontSize="xl" color="gray.800" fontWeight="600">
                  Sign in to your account
                </Heading>
              </VStack>

              <FormControl isRequired>
                <FormLabel
                  color="gray.700"
                  fontSize="sm"
                  fontWeight="600"
                  mb={2}
                >
                  Email address
                </FormLabel>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.300"
                  color="gray.900"
                  _placeholder={{ color: "gray.500" }}
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{
                    borderColor: "teal.500",
                    boxShadow: "0 0 0 3px rgba(56, 178, 172, 0.1)",
                    outline: "none",
                  }}
                  size="md"
                  borderRadius="md"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel
                  color="gray.700"
                  fontSize="sm"
                  fontWeight="600"
                  mb={2}
                >
                  Password
                </FormLabel>
                <InputGroup>
                  <Input
                    type={showPass ? "text" : "password"}
                    placeholder="Enter your password"
                    value={userPass}
                    onKeyDown={handleEnterKeyPress}
                    onChange={(e) => setUserpass(e.target.value)}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    color="gray.900"
                    _placeholder={{ color: "gray.500" }}
                    _hover={{ borderColor: "gray.400" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 3px rgba(56, 178, 172, 0.1)",
                      outline: "none",
                    }}
                    size="md"
                    borderRadius="md"
                  />
                  <InputRightElement h="full">
                    <IconButton
                      variant="ghost"
                      onClick={handleShowPass}
                      icon={showPass ? <HiEyeOff /> : <HiEye />}
                      color="gray.500"
                      _hover={{ color: "gray.700", bg: "gray.100" }}
                      size="sm"
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <Button
                isLoading={loading}
                loadingText="Signing in..."
                size="md"
                width="full"
                onClick={submitLogin}
                bg="teal.500"
                color="white"
                _hover={{ bg: "teal.600" }}
                _active={{ bg: "teal.700" }}
                fontWeight="600"
                borderRadius="md"
                h="10"
              >
                Sign in
              </Button>
            </VStack>
          </Card>

          {/* Sign Up Link */}
          <Card
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            p={4}
            w="100%"
            borderRadius="lg"
            textAlign="center"
          >
            <HStack spacing={1} justify="center" color="gray.600">
              <Text fontSize="sm">New to Vartaverse?</Text>
              <Link
                onClick={gotoSignUpPage}
                color="teal.500"
                fontSize="sm"
                fontWeight="600"
                _hover={{ color: "teal.600", textDecoration: "underline" }}
                cursor="pointer"
              >
                Create an account
              </Link>
            </HStack>
          </Card>

          {/* Back to Home */}
          <Link
            onClick={() => navigate("/")}
            color="teal.500"
            fontSize="sm"
            fontWeight="500"
            _hover={{ color: "teal.600", textDecoration: "underline" }}
            cursor="pointer"
          >
            ← Back to homepage
          </Link>
        </VStack>
      </Container>
    </Box>
  );
};

export default LoginPage;
